import type { ReportColumn } from '../models/column.types'
import type { DataVisibleProps } from '../models/dataVisible.types'
import type { SectionDataVisibleConfig } from '../models/layoutConfig.types'
import type { ScheduleTab, ScheduleTabValue } from '../models/schedule.types'

import { isKeyEventField } from './columns/keyEventFields'
import { resolvePartitionColumns } from './columns/partitionColumns'

// Seeds a section's DataVisible field props from schedule partition metadata — the v3
// `setupTabs` + `getPartitionColumns` derivation (DataVisible.js:109-139, :290-384).
//
// The distinction this module exists to preserve: `tab.columns` is the partition's WHOLE column
// universe, while `tab.dimensions`/`tab.metrics` are only the PRE-SELECTED ones. v3 offers the
// universe and marks the rest unselected (`:337-340`); reading only the pre-selected keys turns
// the customize panel into a remove-only tool, because a field that is not already on screen can
// never be added back (ORP-19849).
//
// The derivation itself now lives in `columns/partitionColumns.ts` (ORP-18643) so the conditional
// injections and the tab-eligibility rules have one home; this module keeps only the section seam
// — which config values win, and what the section can hand the derivation.

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

function isTabDetail(value: ScheduleTabValue): value is ScheduleTab {
  return typeof value === 'object' && value !== null
}

/** Every matched schedule tab detail for a config's `scheduleTabs` target list. */
export function scheduleTabDetails(
  selection: ScheduleTabValue | ScheduleTabValue[] | null,
): ScheduleTab[] {
  const values = Array.isArray(selection) ? selection : [selection]
  return values.filter(isTabDetail)
}

export type ResolvedSectionFields = Pick<DataVisibleProps, 'dimensions' | 'metrics'> & {
  partitionName: string | undefined
  /** The offer universe the customize panel lists; pre-selected fields are a subset. */
  availableFields: string[]
}

/**
 * Resolve a section build output into concrete DataVisible field props. Config-provided values
 * always win; schedule metadata fills the gaps (v3 `setupTabs` first-tab seeding for the
 * pre-selected sets, `getPartitionColumns` tab-union for the offer universe).
 */
function preferredDimensions(
  config: SectionDataVisibleConfig,
  details: ScheduleTab[],
  detail: ScheduleTab | null,
): string[] {
  const preselected = stringArray(detail?.['dimensions'])
  if (!config.preferredDimensions?.length) return preselected
  const enumerated = [...new Set(details.flatMap((tab) => stringArray(tab.columns)))]
  const seeds = new Set(config.preferredDimensions)
  const kept = new Set(preselected)
  return [...preselected, ...enumerated.filter((field) => seeds.has(field) && !kept.has(field))]
}

export interface ResolveSectionFieldsOptions {
  /** Catalog accessor; `showKeyEvents` needs it to keep the selection numeric (v3 `base.numeric`). */
  getColumn?: (field: string) => ReportColumn | null | undefined
}

export function resolveSectionFields(
  config: SectionDataVisibleConfig,
  details: ScheduleTab[],
  options: ResolveSectionFieldsOptions = {},
): ResolvedSectionFields {
  const detail = details[0] ?? null
  // v3 `setupTabs:117-118` applied `hideCols` to the pre-selected sets BEFORE deriving columns, so
  // a hidden field also stops disqualifying tabs that do not enumerate it.
  const hidden = new Set([...(config.hideFields ?? []), ...(config.hideCols ?? [])])
  const keep = (fields: string[]): string[] =>
    hidden.size === 0 ? fields : fields.filter((field) => !hidden.has(field))
  const dimensions = keep(config.dimensions ?? preferredDimensions(config, details, detail))
  const metrics = keep(config.metrics ?? stringArray(detail?.metrics))
  const partitionName = config.partitionName ?? detail?.partition_name ?? undefined

  const availableFields =
    config.availableFields ??
    resolvePartitionColumns({
      tabs: details,
      dimensions,
      metrics,
      partitionName,
      // The section's configured filters, which is what the reach/frequency injection reads.
      // User filters are deliberately excluded: they are applied after the offer is built, and
      // letting them widen it would make the customize panel's contents flicker per filter edit.
      filters: config.filters,
      ...(options.getColumn ? { getColumn: options.getColumn } : {}),
    }).fields

  return {
    dimensions,
    // v3 selected key events out of the whole offered universe, not out of the tab's pre-selected
    // metrics — the point of the flag is to surface columns the partition carries but does not
    // show by default. Appended after `metrics` so the configured order still leads.
    metrics: config.showKeyEvents
      ? [
          ...new Set([
            ...metrics,
            ...availableFields.filter(
              (field) =>
                !hidden.has(field) &&
                isKeyEventField(field) &&
                options.getColumn?.(field)?.numeric === true,
            ),
          ]),
        ]
      : metrics,
    partitionName,
    availableFields,
  }
}
